import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { isEqual, isEmpty } from 'lodash'

import { useSetOneTimeProductMutation, useSetStripeRequestMutation } from 'modules/ProductInfo/productInfoApi'

import { getOneTimeProductSlugs } from 'modules/ProductInfo/utils/oneTimeProductUtils'
import { createUID } from 'common/utils/createUID'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'
import { useDialog } from 'common/components/Dialog/hooks/useDialog'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Input } from 'common/components/Input/Input'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import { schema } from './schema'
import PriceInput from '../PricingForm/PriceInput'
import { MIN_PRODUCT_PRICE } from '../ProductForm/schema'
import { accessUnitOptions } from '../ProductForm/AccessUnitInput'

export default function CreateProductForm({ coachOrgId, currency, oneTimeProductsData }) {
  const [, setDialogOpen] = useDialog()
  const { createAlert } = useAlert()

  const [loading, setLoading] = useState(false)

  const [setStripeRequest] = useSetStripeRequestMutation()
  const [setOneTimeProduct] = useSetOneTimeProductMutation()

  const defaultValues = {
    name: '',
    description: '',
    price: '',
  }

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  })

  const {
    register,
    watch,
    formState: { errors },
    handleSubmit,
    clearErrors,
    trigger,
    setValue,
  } = methods

  const formState = watch()

  const onSubmit = async (data) => {
    const oneTimeProductSlugs = getOneTimeProductSlugs({ oneTimeProductsData, productId: null })
    let defaultSlug = (data.name || '').replace(/[\W_]+/g, '-').toLowerCase()
    if (oneTimeProductSlugs[defaultSlug]) {
      const slugUID = createUID()
        .replace(/[\W_]+/g, '-')
        .toLowerCase()
      defaultSlug = `${defaultSlug}${slugUID}`
    }

    setLoading(true)
    const id = createUID()
    await setOneTimeProduct({
      coachOrgId,
      product: {
        id,
        stripeProductName: data.name || '',
        description: data.description || '',
        price: data.price || '',
        slug: defaultSlug,
        createdAt: Date.now(),
        paymentType: 'one-time',
        currency,
        accessUnit: accessUnitOptions.year,
        accessValue: 2,
        readyForSale: false,
      },
      productId: id,
    })
    await setStripeRequest({
      requestId: id,
      coachOrgId,
      type: 'CREATE_ONE_TIME_PRODUCT',
      payload: {
        trybeProductId: id,
        name: data.name,
        unitAmount: data.price,
        currency,
      },
    })
    setLoading(false)
    setDialogOpen(false)
    createAlert({ text: 'Product created!', type: 'success' })
  }

  // ref control
  const { addManyInputRefs, moveFocusOnKeyPress } = useFormRefsControl()
  const nameRef = useRef()
  const descriptionRef = useRef()
  const priceRef = useRef()

  const submitRef = useRef()

  useEffect(() => {
    addManyInputRefs([
      { ref: nameRef, name: 'name' },
      { ref: descriptionRef, name: 'description' },
      { ref: priceRef, name: 'price' },
      { ref: submitRef, name: 'submit' },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, handleSubmit(onSubmit)))

  return (
    <FormProvider {...methods}>
      <div className='divide-y divide-gray-200 overflow-auto'>
        <form onSubmit={handleSubmit(onSubmit)} id='productForm'>
          <div className='flex flex-col px-10 py-4 border-b border-gray-200'>
            <div className='mb-2'>
              <label htmlFor='name' className='inline-flex cursor-pointer font-semibold text-tBlack mb-1'>
                Name
              </label>
              <Input
                name='name'
                type='text'
                placeholder='Enter name'
                register={register}
                inputRef={nameRef}
                error={errors?.name?.message}
                autoFocus={true}
              />
              {errors.name && <p className='flex text-xs mt-1 text-tRed'>{errors.name.message}</p>}
            </div>
            <div className='mb-2'>
              <label htmlFor='description' className='inline-flex cursor-pointer font-semibold text-tBlack mb-1'>
                Description
              </label>
              <Input
                name='description'
                type='text'
                placeholder='Enter description'
                register={register}
                inputRef={descriptionRef}
                error={errors?.description?.message}
              />
              {errors.description && <p className='flex text-xs mt-1 text-tRed'>{errors.description.message}</p>}
            </div>
          </div>
          <div className='flex flex-col px-10 py-4 '>
            <div className='mb-2'>
              <label htmlFor='price' className='inline-flex cursor-pointer font-semibold text-tBlack mb-1'>
                Price
              </label>
              <PriceInput
                inputRef={priceRef}
                name='price'
                placeholder='0.00'
                value={formState.price}
                onChange={(price) => {
                  if (errors?.price?.message && price >= MIN_PRODUCT_PRICE) {
                    clearErrors('price')
                  } else if (price < MIN_PRODUCT_PRICE) {
                    trigger('price')
                  }
                  setValue('price', Number(price))
                }}
                error={Boolean(errors?.price?.message)}
                currency={currency}
              />
              {errors.price && <p className='flex text-xs mt-1 text-tRed'>{errors.price.message}</p>}
            </div>
          </div>
        </form>
      </div>
      <UpdateActions
        handleSubmit={handleSubmit}
        loading={loading}
        actionText='Create'
        disabled={isEqual(defaultValues, formState) || !isEmpty(errors)}
        form='productForm'
        ref={submitRef}
        hideDelete={true}
        onKeyUp={(e) => {
          if (e.code === 'Enter') {
            moveFocusOnKeyPress(e, handleSubmit(onSubmit))
          }
        }}
      />
    </FormProvider>
  )
}
